import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "@athena/ui/lib/utils";

export type AvatarSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

const avatarSizeMap: Record<AvatarSize, string> = {
    xxs: "h-4 w-4 text-[8px]",
    xs: "h-6 w-6 text-[10px]",
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg",
};

const AvatarSquareContext = React.createContext(false);

export interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
    active?: boolean;
    size?: AvatarSize;
    square?: boolean;
}

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
    ({ className, active, size = "md", square = false, ...props }, ref) => (
        <AvatarSquareContext.Provider value={square}>
            <AvatarPrimitive.Root
                ref={ref}
                className={cn(
                    "relative flex shrink-0",
                    square ? "rounded-md" : "rounded-full",
                    avatarSizeMap[size],
                    active && "ring-success ring-offset-background ring-2 ring-offset-2",
                    className,
                )}
                {...props}
            />
        </AvatarSquareContext.Provider>
    ),
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Image>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => {
    const square = React.useContext(AvatarSquareContext);
    return (
        <AvatarPrimitive.Image
            ref={ref}
            className={cn(
                "aspect-square h-full w-full overflow-hidden",
                square ? "rounded-md" : "rounded-full",
                className,
            )}
            {...props}
        />
    );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Fallback>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => {
    const square = React.useContext(AvatarSquareContext);
    return (
        <AvatarPrimitive.Fallback
            ref={ref}
            className={cn(
                "bg-muted flex h-full w-full items-center justify-center",
                square ? "rounded-md" : "rounded-full",
                className,
            )}
            {...props}
        />
    );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export interface AvatarGradientFallbackProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
    from?: string;
    to?: string;
}

const AvatarGradientFallback = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Fallback>,
    AvatarGradientFallbackProps
>(({ className, from = "from-secondary", to = "to-primary", ...props }, ref) => {
    const square = React.useContext(AvatarSquareContext);
    return (
        <AvatarPrimitive.Fallback
            ref={ref}
            className={cn(
                "text-primary-foreground flex h-full w-full items-center justify-center bg-gradient-to-br font-semibold",
                square ? "rounded-md" : "rounded-full",
                from,
                to,
                className,
            )}
            {...props}
        />
    );
});
AvatarGradientFallback.displayName = "AvatarGradientFallback";

export interface AvatarBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {}

const AvatarBadge = React.forwardRef<HTMLSpanElement, AvatarBadgeProps>(({ className, ...props }, ref) => (
    <span
        ref={ref}
        className={cn(
            "border-background bg-success absolute right-0 bottom-0 h-3 w-3 rounded-full border-2",
            className,
        )}
        {...props}
    />
));
AvatarBadge.displayName = "AvatarBadge";

export { Avatar, AvatarImage, AvatarFallback, AvatarGradientFallback, AvatarBadge };
